import apiClient from "@/services/axios";
import { mapState } from "vuex";
import { getSchoolType, getAvailableLevels } from "@/utilities/settings";

export default {
  computed: {
    ...mapState(["settings", "user"]),
    schoolDetails() {
      return (
        this.user.building.filter(
          (el) => el.dbName == this.settings.activeBuilding
        )[0] || {}
      );
    },
  },
  data() {
    return {
      classes: [],
      classesLoading: false,
      subsubjects: [],
      markTypesKeys: ["oral", "cont1", "cont2", "synth", "written", "tp"],
    };
  },
  methods: {
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    async loadClasses() {
      this.classesLoading = true;
      try {

        const schoolType = getSchoolType(this.schoolDetails) || getSchoolType(this.user, this.settings);
        if (!schoolType) {
          this.$message.warning(this.$t("bulletin.saisirSchoolType"));
          this.classes = [];
          return;
        }

        const availableLevels = getAvailableLevels(this.$t.bind(this), schoolType, this.settings?.activeBuilding?.countryCode);


        if (!availableLevels || availableLevels.length === 0) {
          this.$message.warning(this.$t("bulletin.noLevelsAvailable"));
          this.classes = [];
          return;
        }

        const res = await apiClient.post("/classrooms/filter", {
          query: {
            status: "active",
            level: { $lte: Math.max(...availableLevels), $gte: Math.min(...availableLevels) },
          },
          aggregation: {
            $project: {
              _id: 1,
              name: 1,
              level: 1,
              bulletinCollege: 1,
            },
          },
        });
        this.classes = res.data || [];
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.classesLoading = false;
      }
    },
    getSubsubjectName(subsubjectId) {
      if (!subsubjectId) return "";
      const subj = this.subsubjects.find((s) => s._id === subsubjectId);
      return subj ? subj.name : "";
    },
    async loadSubsubjects() {
      try {
        const subjRes = await apiClient.post("/subsubject/filter", {
          query: { status: "active" },
        });
        this.subsubjects = subjRes.data || [];
      } catch (e) {
        // Silently fail, subsubjects are optional
      }
    },
    getCurrentLevel(selectedClasse) {
      if (!selectedClasse) return null;
      const classe = this.classes.find((c) => c._id === selectedClasse);
      return classe ? classe.level : null;
    },
    async loadConfig(level, trimester) {
      if (!level) return [];
      try {
        const res = await apiClient.post("/bulletin/college/config/subjects", {
          level: parseInt(level),
          trimester: trimester ? parseInt(trimester) : null,
        });

        // Load subsubjects for display
        await this.loadSubsubjects();

        return (res.data || []).map((subj) => ({
          _id: subj.subject._id || subj.subject,
          name: subj.subject.name,
          coef: subj.coef,
          markTypes: subj.markTypes || {},
        }));
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
        return [];
      }
    },
    async loadStudents(classroomId) {
      if (!classroomId) return [];
      try {
        let match = {};
        match[`schoolarYearsHistory.${this.settings.activeSchoolarYear}`] =
          classroomId;

        const res = await apiClient.post("/students/filter", {
          query: {},
          aggregation: [
            {
              $match: match,
            },
            {
              $set: {
                classRoom: {
                  $convert: {
                    input: `$schoolarYearsHistory.${this.settings.activeSchoolarYear}`,
                    to: "objectId",
                    onError: null,
                    onNull: null,
                  },
                },
              },
            },
            {
              $lookup: {
                from: "classrooms",
                localField: "classRoom",
                foreignField: "_id",
                as: "classroom",
              },
            },
            {
              $project: {
                _id: 1,
                firstName: 1,
                middleName: 1,
                lastName: 1,
                firstNameLower: { $toLower: "$firstName" },
                gender: 1,
                classroom: {
                  _id: 1,
                  name: 1,
                },
              },
            },
            {
              $sort: {
                firstNameLower: 1,
              },
            },
          ],
        });

        const students = [];
        (res.data || []).forEach((elem) => {
          try {
            students.push({
              _id: elem._id,
              key: elem._id,
              fullName: elem.firstName + (elem.middleName ? " " + elem.middleName : "") + " " + elem.lastName,
              classroom:
                elem.classroom && elem.classroom[0]
                  ? elem.classroom[0].name
                  : "",
              classroom_id:
                elem.classroom && elem.classroom[0]
                  ? elem.classroom[0]._id
                  : null,
              gender: elem.gender,
            });
          } catch (e) {
            // Skip invalid entries
          }
        });
        return students;
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
        return [];
      }
    },
    normalizeMarksArray(markDoc, subjectConfig, markTypeKey) {
      const markTypeConfig = subjectConfig?.markTypes?.[markTypeKey];
      const count = markTypeConfig?.count || 1;
      const existingMarks = markDoc?.marks?.[markTypeKey] || [];

      if (existingMarks.length < count) {
        const result = [...existingMarks];
        while (result.length < count) {
          result.push(null);
        }
        return result;
      } else if (existingMarks.length > count) {
        return existingMarks.slice(0, count);
      }
      return existingMarks;
    },
    normalizeAllMarks(markDoc, subjectConfig) {
      const normalizedMarks = {
        oral: [],
        cont1: [],
        cont2: [],
        synth: [],
        written: [],
        tp: [],
      };

      Object.keys(normalizedMarks).forEach((markTypeKey) => {
        normalizedMarks[markTypeKey] = this.normalizeMarksArray(
          markDoc,
          subjectConfig,
          markTypeKey
        );
      });

      return normalizedMarks;
    },
    async saveMarksBulk(marksToSave) {
      try {
        await apiClient.post("/bulletin/college/mark/bulk", {
          marks: marksToSave,
        });
        return true;
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errorCode) {
          this.$message.error(
            this.$t(`errorCode.${e.response.data.errorCode}`)
          );
        } else {
          this.$message.error(this.$t("error.erreur"));
        }
        return false;
      }
    },
    validateMarks(markData) {
      for (const mark of markData) {
        if (mark.marks) {
          for (const [markType, marksArray] of Object.entries(mark.marks)) {
            if (Array.isArray(marksArray)) {
              for (const value of marksArray) {
                if (
                  value !== null &&
                  value !== undefined &&
                  (value < 0 || value > 20)
                ) {
                  return false;
                }
              }
            }
          }
        }
      }
      return true;
    },
    // Common method to create default exemptions object
    createDefaultExemptions() {
      return {
        oral: false,
        cont1: false,
        cont2: false,
        synth: false,
        written: false,
        tp: false,
      };
    },
    // Common method to create default marks object
    createDefaultMarks() {
      return {
        oral: [],
        cont1: [],
        cont2: [],
        synth: [],
        written: [],
        tp: [],
      };
    },
    // Common method for handleBeforeUnload
    handleBeforeUnload(event) {
      if (this.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = this.$t("all.unsavedchangesContent");
        return this.$t("all.unsavedchangesContent");
      }
    },
    // Common method to check for unsaved changes and show confirmation
    async checkUnsavedChanges() {
      if (!this.hasUnsavedChanges) {
        return true;
      }

      const _this = this;
      const confirmed = await new Promise((resolve) => {
        this.$confirm({
          title: this.$t("all.unsavedchanges"),
          content: this.$t("all.unsavedchangesContent"),
          okText: this.$t("all.oui"),
          okType: "danger",
          cancelText: this.$t("action.annuler"),
          onOk() {
            _this.hasUnsavedChanges = false;
            _this.$emit("unsaved-changes", false);
            resolve(true);
          },
          onCancel() {
            resolve(false);
          },
        });
      });
      return confirmed;
    },
    // Common method to reset unsaved changes state
    resetUnsavedChanges() {
      this.hasUnsavedChanges = false;
      this.$emit("unsaved-changes", false);
    },
    // Common method to mark as having unsaved changes
    markAsUnsaved() {
      this.hasUnsavedChanges = true;
      this.$emit("unsaved-changes", true);
    },
    // Generic method to find mark in markData
    findMark(studentId, subjectId = null) {
      if (subjectId) {
        // ByStudent: find by both student and subject
        return this.markData.find(
          (m) => m.student === studentId && m.subject === subjectId
        );
      } else {
        // BySubject: find by student only (subject is already selected)
        return this.markData.find((m) => m.student === studentId);
      }
    },
    // Generic method to check if exempted
    isExemptedGeneric(studentId, markTypeKey, subjectId = null) {
      const mark = this.findMark(studentId, subjectId);
      return mark?.exemptions?.[markTypeKey] || false;
    },
    // Generic method to get marks array
    getMarksArrayGeneric(studentId, markTypeKey, count, subjectId = null) {
      const mark = this.findMark(studentId, subjectId);

      // Determine count if not provided
      if (!count) {
        if (subjectId) {
          // ByStudent: get count from subjectList
          const subject = this.subjectList?.find(s => s._id === subjectId);
          if (subject && subject.markTypes && subject.markTypes[markTypeKey]) {
            count = subject.markTypes[markTypeKey].count || 1;
          } else {
            count = 1;
          }
        } else {
          // BySubject: get count from selectedSubjectConfig
          const markTypeConfig = this.selectedSubjectConfig?.markTypes?.[markTypeKey];
          count = markTypeConfig?.count || 1;
        }
      }

      if (!mark) {
        return Array(count).fill(null);
      }

      if (!mark.marks) {
        this.$set(mark, 'marks', this.createDefaultMarks());
      }

      if (!Array.isArray(mark.marks[markTypeKey])) {
        this.$set(mark.marks, markTypeKey, Array(count).fill(null));
      } else if (mark.marks[markTypeKey].length !== count) {
        const currentArray = mark.marks[markTypeKey];
        if (currentArray.length < count) {
          while (currentArray.length < count) {
            currentArray.push(null);
          }
        } else if (currentArray.length > count) {
          currentArray.splice(count);
        }
        this.$set(mark.marks, markTypeKey, currentArray);
      }

      return mark.marks[markTypeKey];
    },
    // Generic method to create a new mark object
    createNewMarkObject(studentId, subjectId, markTypeConfig = null) {
      const obj = {
        student: studentId,
        subject: subjectId,
        marks: this.createDefaultMarks(),
        exemptions: this.createDefaultExemptions(),
        subsubject: markTypeConfig?.subsubject || null,
      };

      // Initialize marks arrays based on subject config
      const subjectConfig = subjectId
        ? (this.subjectList?.find(s => s._id === subjectId) || this.selectedSubjectConfig)
        : this.selectedSubjectConfig;

      Object.keys(obj.marks).forEach((key) => {
        const mtConfig = subjectConfig?.markTypes?.[key];
        const mtCount = mtConfig?.count || 1;
        obj.marks[key] = Array(mtCount).fill(null);
      });

      return obj;
    },
    // Generic method to update mark at index
    updateMarkAtIndexGeneric(
      newMark,
      studentId,
      markTypeKey,
      index,
      markTypeConfig,
      subjectId = null
    ) {
      let mark = this.findMark(studentId, subjectId);

      if (!mark) {
        const actualSubjectId = subjectId || this.selectedSubject;
        const obj = this.createNewMarkObject(studentId, actualSubjectId, markTypeConfig);
        this.markData.push(obj);
        mark = this.markData[this.markData.length - 1];
      }

      if (!mark.marks) {
        this.$set(mark, "marks", this.createDefaultMarks());
      }

      const count = markTypeConfig?.count || 1;
      if (!Array.isArray(mark.marks[markTypeKey])) {
        this.$set(mark.marks, markTypeKey, Array(count).fill(null));
      } else if (mark.marks[markTypeKey].length !== count) {
        const currentArray = mark.marks[markTypeKey];
        if (currentArray.length < count) {
          while (currentArray.length < count) {
            currentArray.push(null);
          }
        } else {
          currentArray.splice(count);
        }
        this.$set(mark.marks, markTypeKey, currentArray);
      }

      if (index >= 0 && index < mark.marks[markTypeKey].length) {
        this.$set(
          mark.marks[markTypeKey],
          index,
          newMark !== null && newMark !== undefined ? Number(newMark) : null
        );
        this.markAsUnsaved();
      }
    },
    // Generic method to exempt mark type
    async exemptMarkTypeGeneric(
      studentId,
      markTypeKey,
      exempted,
      subjectId = null
    ) {
      const mark = this.findMark(studentId, subjectId);

      if (!mark) {
        const actualSubjectId = subjectId || this.selectedSubject;
        const obj = this.createNewMarkObject(studentId, actualSubjectId);
        obj.exemptions[markTypeKey] = exempted;
        this.markData.push(obj);
      } else {
        if (!mark.exemptions) {
          this.$set(mark, "exemptions", this.createDefaultExemptions());
        }
        this.$set(mark.exemptions, markTypeKey, exempted);
      }

      try {
        const markToSave = mark || this.findMark(studentId, subjectId);
        const actualSubjectId = subjectId || this.selectedSubject;
        const actualTrimester = this.selectedTrimester || "1";
        const actualClasse = this.selectedClasse;

        await apiClient.put("/bulletin/college/mark", {
          student: studentId,
          subject: actualSubjectId,
          markType: markTypeKey,
          exempted: exempted,
          trimester: parseInt(actualTrimester),
          classroom: actualClasse,
        });

        if (markToSave && !markToSave._id) {
          // If mark was just created, refresh to get _id
          if (this.updateMarks) {
            await this.updateMarks();
          }
        }

        this.$message.success(
          exempted
            ? this.$t("success.studentExempted")
            : this.$t("success.studentExemptionRemoved")
        );
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errorCode) {
          this.$message.error(
            this.$t(`errorCode.${e.response.data.errorCode}`)
          );
        } else {
          this.$message.error(this.$t("error.erreur"));
        }
      }
    },
    // Generic method to calculate moyenne
    // componentType: 'byStudent' or 'bySubject'
    // identifier: subjectId for 'byStudent', studentId for 'bySubject'
    calculateMoyenneGeneric(componentType, identifier) {
      let actualSubjectId;
      let actualStudentId;

      if (componentType === 'byStudent') {
        // ByStudent: identifier is subjectId, studentId comes from this.selectedStudent
        actualSubjectId = identifier;
        actualStudentId = this.selectedStudent;
      } else if (componentType === 'bySubject') {
        // BySubject: identifier is studentId, subjectId comes from this.selectedSubject
        actualSubjectId = this.selectedSubject;
        actualStudentId = identifier;
      } else {
        return "--";
      }

      if (!actualStudentId || !actualSubjectId) {
        return "--";
      }

      const subject = componentType === 'byStudent'
        ? this.subjectList?.find((s) => s._id === actualSubjectId)
        : this.selectedSubjectConfig;

      if (!subject || !subject.markTypes) {
        return "--";
      }

      const mark = this.findMark(actualStudentId, actualSubjectId);

      if (!mark || !mark.marks) {
        return "--";
      }

      let totalWeightedSum = 0;
      let totalCoef = 0;

      // Iterate through all markTypes
      this.markTypesKeys.forEach((markTypeKey) => {
        const markTypeConfig = subject.markTypes[markTypeKey];

        // Skip if markType is not enabled or exempted
        if (!markTypeConfig || !markTypeConfig.enabled) {
          return;
        }

        if (this.isExemptedGeneric(actualStudentId, markTypeKey, actualSubjectId)) {
          return;
        }

        const marksArray = mark.marks[markTypeKey];
        if (!marksArray || !Array.isArray(marksArray)) {
          return;
        }

        // Filter out null values
        const validMarks = marksArray.filter(
          (m) => m !== null && m !== undefined
        );

        if (validMarks.length === 0) {
          return;
        }

        // Calculate average for this markType
        let markTypeAverage;
        if (markTypeConfig.count > 1) {
          // If count > 1, average all marks
          const sum = validMarks.reduce((acc, m) => acc + Number(m), 0);
          markTypeAverage = sum / validMarks.length;
        } else {
          // Single mark
          markTypeAverage = Number(validMarks[0]);
        }

        // Get coefficient
        const coef = markTypeConfig.coef || 1;

        // Add to weighted sum
        totalWeightedSum += markTypeAverage * coef;
        totalCoef += coef;
      });

      if (totalCoef === 0) {
        return "--";
      }

      // Calculate final average
      const moyenne = totalWeightedSum / totalCoef;

      // Round to 2 decimal places
      return moyenne.toFixed(2);
    },
    // Generic method to save all marks
    async saveAllGeneric(getSubjectConfig) {
      this.saveLoading = true;

      if (!this.validateMarks(this.markData)) {
        this.$message.warning(this.$t("warning.notCorrectMarks"));
        this.saveLoading = false;
        return;
      }

      const marksToSave = [];

      for (const mark of this.markData) {
        const subject = getSubjectConfig
          ? getSubjectConfig(mark.subject)
          : this.selectedSubjectConfig;
        const normalizedMarks = this.normalizeAllMarks(mark, subject);

        const markData = {
          student: mark.student,
          subject: mark.subject,
          marks: normalizedMarks,
          exemptions: mark.exemptions || this.createDefaultExemptions(),
          trimester: parseInt(this.selectedTrimester),
          classroom: this.selectedClasse,
          subsubject: mark.subsubject?._id || null,
        };

        marksToSave.push(markData);
      }

      const success = await this.saveMarksBulk(marksToSave);
      if (success) {
        if (this.updateMarks) {
          await this.updateMarks();
        }
        this.resetUnsavedChanges();
        this.$message.success(this.$t("success.noteToutesEnregistre"));
      }
      this.saveLoading = false;
    },
  },
};

